import { useMemo } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  AircraftCatalog,
  ArrivalInquiry,
  EventPlacement,
  PlacementAlgorithm,
  ZoneGeometry,
  ZoneSlotGrid,
} from '../engine/types'
import { sortInquiries } from '../engine/sortInquiries'
import { scheduleReuse, peakConcurrent } from '../engine/scheduleReuse'
import { buildZoneSlotGrids } from '../engine/buildSlotGrid'
import { generateSyntheticInquiries, type SyntheticOptions } from '../data/syntheticInquirySource'
import { DEMO_SCENARIOS, DEFAULT_SCENARIO_ID, type DemoScenario } from '../data/scenarios'
import { DEFAULT_MOTION_CONFIG, type MotionConfig } from '../data/motionConfig'
import { DEFAULT_EVENT_PRICING, type EventPricing, type ZonePricingOverride } from '../data/eventPricing'
import { type EventStats, type PeriodRange, computeEventStats } from '../engine/eventStats'
import {
  WINDOW_START,
  WINDOW_END,
  DEFAULT_VIEW_TIME,
  PERSIST_KEY,
  SIM_MS_PER_REAL_MS_AT_1X,
  MIN_SPEED,
  MAX_SPEED,
} from '../data/eventWindow'

type Playback = 'idle' | 'playing' | 'paused'

interface PersistedState {
  windowStart: number
  windowEnd: number
  currentTime: number
  speed: number
  algorithm: PlacementAlgorithm
  algorithmDirty: boolean
  /** how far the auto-placer has advanced through the ordered queue */
  processedCount: number
  /** keyed by eventAllocationId (`evt-<scheduleId>`) */
  placements: Record<string, EventPlacement>
  /** Active scenario id (built-in or custom); drives the modal's selected preset. */
  activeScenarioId: string
  /** Live-editable fleet-generation options (count, mix, curves, seed). */
  scenarioOptions: SyntheticOptions
  /** User-saved scenarios keyed by id (survive reload via persist). */
  customScenarios: Record<string, DemoScenario>
}

interface RuntimeState {
  ready: boolean
  playback: Playback
  inquiriesById: Record<string, ArrivalInquiry>
  orderedScheduleIds: string[]
  zones: ZoneGeometry[]
  /** precomputed uniform slot grids per zone (fillPriority order) */
  slotGrids: ZoneSlotGrid[]
  /** total fixed slots across all zones = concurrent capacity */
  slotCapacity: number
  /**
   * DB-loaded base dimensions per area — the width/depth the rendered footprint
   * mesh was built at (scale 1). The zone editor resizes the live footprint by
   * scaling it `current / base`, so this stays fixed for the session (captured at
   * load, re-captured only on a full reload).
   */
  zoneBaseGeom: Record<string, { width: number; depth: number }>
  catalog: AircraftCatalog
  selectedAllocationId: string | null
  /** Zone whose placement settings are being inspected/edited (drives label highlight + camera focus). */
  selectedZoneId: string | null
  /** Animation tuning knobs (not persisted — demo defaults each session). */
  motionConfig: MotionConfig
  /** Areas with live placement-setting edits not yet saved to the DB. */
  zoneDirty: Set<string>
  /** Live-editable event pricing rate card (not persisted — demo default each session). */
  pricing: EventPricing
  /** Per-zone pricing overrides keyed by areaId (not persisted). */
  zonePricingOverrides: Record<string, ZonePricingOverride>
  /** Time window the stats + capacity views are scoped to (defaults to whole event). */
  statsPeriod: PeriodRange
  /**
   * Bumped to force a full teardown + rebuild of the 3D stage (every event
   * model is disposed and re-created at the current clock time). Purely a
   * render-layer signal — placements, timeline and scenario are untouched — so
   * it is runtime-only and never persisted.
   */
  stageEpoch: number
}

interface Actions {
  /** Seed runtime data (called by the bootstrap hook). Persisted progress is kept. */
  hydrateRuntime(inquiries: ArrivalInquiry[], zones: ZoneGeometry[], catalog: AircraftCatalog): void
  /** Live-edit the scenario working copy (no regen until regenerateFleet). */
  setScenarioOptions(patch: Partial<SyntheticOptions>): void
  /** Load a built-in or custom scenario's options into the working copy. */
  selectScenario(id: string): void
  /** Snapshot the current working copy as a named custom scenario. Returns its id. */
  saveCustomScenario(name: string): string
  /** Remove a custom scenario. */
  deleteCustomScenario(id: string): void
  /** Regenerate the fleet from the working copy + reset placements. */
  regenerateFleet(): void
  /** Live-edit one zone's placement settings: rebuild grids + re-derive placements. */
  updateZoneSettings(areaId: string, patch: Partial<ZoneGeometry>): void
  /** Drop a zone's unsaved-edit flag (after a successful DB save). */
  clearZoneDirty(areaId: string): void
  setAlgorithm(a: PlacementAlgorithm): void
  rearrange(): void
  autoPlaceNext(): void
  autoPlaceBlock(size: number): void
  autoPlaceAll(): void
  resetPlacement(): void
  reassign(eventAllocationId: string): void
  confirmAircraft(eventAllocationId: string): void
  confirmZone(areaId: string): void
  select(eventAllocationId: string | null): void
  /** Set the zone being inspected (label click / panel dropdown). */
  setSelectedZoneId(areaId: string | null): void
  // timeline
  play(): void
  pause(): void
  seek(t: number): void
  setSpeed(n: number): void
  tick(realDeltaMs: number): void
  setMotionConfig(patch: Partial<MotionConfig>): void
  /** Replace the pricing rate card (the modal builds the full next object). */
  setPricing(pricing: EventPricing): void
  /** Set or clear (null) a per-zone pricing override. */
  setZonePricingOverride(areaId: string, override: ZonePricingOverride | null): void
  /** Scope the stats + capacity views to a time window. */
  setStatsPeriod(period: PeriodRange): void
  /** Purge and rebuild every 3D model of the stage at the current clock time. */
  resetStage(): void
}

type State = PersistedState & RuntimeState & Actions

const allocId = (scheduleId: string) => `evt-${scheduleId}`

/** Debounce window (ms) for the expensive full re-place during continuous zone edits. */
const ZONE_REPLACE_DEBOUNCE_MS = 200

/** Module-level trailing-debounce handle for `updateZoneSettings`' re-place. */
let replaceTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Recompute placements for the first `count` inquiries of the ordered queue.
 * The time-aware scheduler is deterministic and processes in queue order, so the
 * prefix is stable; we just re-derive and preserve `confirmed` flags by scheduleId.
 * A slot may host different aircraft over time (reuse) — each is its own placement
 * with its own [arrival, departure), and the scene shows whichever is current.
 */
function derivePlacements(
  count: number,
  state: Pick<State, 'orderedScheduleIds' | 'inquiriesById' | 'slotGrids' | 'catalog' | 'placements'>
): { placements: Record<string, EventPlacement>; processedCount: number } {
  const k = Math.max(0, Math.min(count, state.orderedScheduleIds.length))
  const ordered = state.orderedScheduleIds.slice(0, k).map((id) => state.inquiriesById[id])
  const result = scheduleReuse(ordered, state.slotGrids, state.catalog)

  const confirmedBySchedule = new Map<string, boolean>()
  for (const p of Object.values(state.placements)) confirmedBySchedule.set(p.scheduleId, p.confirmed)

  const placements: Record<string, EventPlacement> = {}
  for (const slot of result.slots) {
    const inq = state.inquiriesById[slot.scheduleId]
    if (!inq) continue
    const id = allocId(slot.scheduleId)
    placements[id] = {
      eventAllocationId: id,
      scheduleId: slot.scheduleId,
      aircraftId: inq.aircraftId,
      tailNumber: inq.tailNumber,
      typeId: state.catalog.get(inq.typeCode)?.typeId ?? '',
      typeCode: inq.typeCode,
      position: slot.position,
      rotation: slot.rotationDeg,
      areaId: slot.areaId,
      laneIndex: slot.laneIndex,
      arrival: inq.arrival,
      departure: inq.departure,
      confirmed: confirmedBySchedule.get(slot.scheduleId) ?? false,
      processed: true,
    }
  }
  return { placements, processedCount: k }
}

/**
 * Compute the runtime slice (inquiry index, sorted queue, slot grids, capacity)
 * from a fleet + zones + catalog. Shared by `hydrateRuntime` (bootstrap) and
 * `regenerateFleet` (scenario apply) so both derive identical runtime state.
 */
function buildRuntime(
  inquiries: ArrivalInquiry[],
  zones: ZoneGeometry[],
  catalog: AircraftCatalog,
  algorithm: PlacementAlgorithm
) {
  const inquiriesById: Record<string, ArrivalInquiry> = {}
  for (const inq of inquiries) inquiriesById[inq.scheduleId] = inq
  const orderedScheduleIds = sortInquiries(inquiries, algorithm, catalog).map((i) => i.scheduleId)
  const slotGrids = buildZoneSlotGrids(zones, inquiries, catalog)
  const slotCapacity = slotGrids.reduce((sum, g) => sum + g.slots.length, 0)
  return { inquiriesById, orderedScheduleIds, zones, slotGrids, slotCapacity, catalog }
}

export const useEventPlannerStore = create<State>()(
  persist(
    (set, get) => ({
      // persisted defaults
      windowStart: WINDOW_START,
      windowEnd: WINDOW_END,
      currentTime: DEFAULT_VIEW_TIME,
      speed: 1,
      algorithm: 'arrival',
      algorithmDirty: false,
      processedCount: 0,
      placements: {},
      activeScenarioId: DEFAULT_SCENARIO_ID,
      scenarioOptions: DEMO_SCENARIOS[DEFAULT_SCENARIO_ID].options,
      customScenarios: {},

      // runtime defaults
      ready: false,
      playback: 'idle',
      inquiriesById: {},
      orderedScheduleIds: [],
      zones: [],
      slotGrids: [],
      slotCapacity: 0,
      zoneBaseGeom: {},
      catalog: new Map(),
      selectedAllocationId: null,
      selectedZoneId: null,
      motionConfig: DEFAULT_MOTION_CONFIG,
      zoneDirty: new Set<string>(),
      pricing: DEFAULT_EVENT_PRICING,
      zonePricingOverrides: {},
      statsPeriod: { start: WINDOW_START, end: WINDOW_END },
      stageEpoch: 0,

      hydrateRuntime: (inquiries, zones, catalog) => {
        // Snapshot the as-loaded footprint dimensions so the zone editor can scale
        // the rendered area live (current / base). Captured once per load; never
        // mutated by edits, so the scale stays correct for the whole session.
        const zoneBaseGeom: Record<string, { width: number; depth: number }> = {}
        for (const z of zones) zoneBaseGeom[z.areaId] = { width: z.width, depth: z.depth }
        set({
          ...buildRuntime(inquiries, zones, catalog, get().algorithm),
          zoneBaseGeom,
          ready: true,
          zoneDirty: new Set<string>(),
        })
      },

      setScenarioOptions: (patch) => {
        set({ scenarioOptions: { ...get().scenarioOptions, ...patch } })
      },

      selectScenario: (id) => {
        const scenario = get().customScenarios[id] ?? DEMO_SCENARIOS[id]
        if (!scenario) return
        set({ activeScenarioId: id, scenarioOptions: { ...scenario.options } })
      },

      saveCustomScenario: (name) => {
        const id = `custom-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'scenario'}`
        const scenario: DemoScenario = {
          id,
          name,
          description: 'Custom scenario',
          options: { ...get().scenarioOptions },
          custom: true,
        }
        set({
          customScenarios: { ...get().customScenarios, [id]: scenario },
          activeScenarioId: id,
        })
        return id
      },

      deleteCustomScenario: (id) => {
        const next = { ...get().customScenarios }
        delete next[id]
        const activeScenarioId =
          get().activeScenarioId === id ? DEFAULT_SCENARIO_ID : get().activeScenarioId
        set({ customScenarios: next, activeScenarioId })
      },

      regenerateFleet: () => {
        const { catalog, zones, scenarioOptions, algorithm } = get()
        const inquiries = generateSyntheticInquiries(catalog, scenarioOptions)
        set({
          ...buildRuntime(inquiries, zones, catalog, algorithm),
          placements: {},
          processedCount: 0,
          algorithmDirty: false,
          selectedAllocationId: null,
          ready: true,
          zoneDirty: new Set<string>(),
          // Every actor identity is replaced — rebuild the stage rather than
          // leaving the old fleet's models parked in the retained pool.
          stageEpoch: get().stageEpoch + 1,
        })
      },

      updateZoneSettings: (areaId, patch) => {
        const { zones, inquiriesById, catalog, zoneDirty } = get()
        const nextZones = zones.map((z) => (z.areaId === areaId ? { ...z, ...patch } : z))
        const fleet = Object.values(inquiriesById)
        const slotGrids = buildZoneSlotGrids(nextZones, fleet, catalog)
        const slotCapacity = slotGrids.reduce((sum, g) => sum + g.slots.length, 0)
        const dirty = new Set(zoneDirty)
        dirty.add(areaId)
        // Grids + capacity update instantly (cheap) so the readout tracks the slider.
        set({ zones: nextZones, slotGrids, slotCapacity, zoneDirty: dirty })
        // The full re-place (which feeds the motion plans + scene rebuild) is heavier,
        // so coalesce rapid edits (e.g. dragging the heading slider) behind a trailing
        // debounce — the scene rearranges once per settle rather than per pixel.
        if (replaceTimer) clearTimeout(replaceTimer)
        replaceTimer = setTimeout(() => {
          replaceTimer = null
          set(derivePlacements(get().processedCount, get()))
        }, ZONE_REPLACE_DEBOUNCE_MS)
      },

      clearZoneDirty: (areaId) => {
        const dirty = new Set(get().zoneDirty)
        dirty.delete(areaId)
        set({ zoneDirty: dirty })
      },

      setAlgorithm: (a) => {
        const { inquiriesById, catalog, processedCount } = get()
        const inquiries = Object.values(inquiriesById)
        const ordered = sortInquiries(inquiries, a, catalog).map((i) => i.scheduleId)
        set({ algorithm: a, orderedScheduleIds: ordered, algorithmDirty: processedCount > 0 })
      },

      rearrange: () => {
        const next = derivePlacements(get().processedCount, get())
        set({ ...next, algorithmDirty: false })
      },

      autoPlaceNext: () => {
        const next = derivePlacements(get().processedCount + 1, get())
        set(next)
      },

      autoPlaceBlock: (size) => {
        const next = derivePlacements(get().processedCount + Math.max(1, size), get())
        set(next)
      },

      autoPlaceAll: () => {
        const next = derivePlacements(get().orderedScheduleIds.length, get())
        set(next)
      },

      resetPlacement: () =>
        set({
          placements: {},
          processedCount: 0,
          algorithmDirty: false,
          // Clearing the field should leave nothing resident behind it.
          stageEpoch: get().stageEpoch + 1,
        }),

      reassign: (eventAllocationId) => {
        const placements = { ...get().placements }
        delete placements[eventAllocationId]
        set({
          placements,
          selectedAllocationId:
            get().selectedAllocationId === eventAllocationId ? null : get().selectedAllocationId,
        })
      },

      select: (eventAllocationId) => set({ selectedAllocationId: eventAllocationId }),

      setSelectedZoneId: (areaId) => set({ selectedZoneId: areaId }),

      confirmAircraft: (eventAllocationId) => {
        const existing = get().placements[eventAllocationId]
        if (!existing) return
        set({ placements: { ...get().placements, [eventAllocationId]: { ...existing, confirmed: true } } })
      },

      confirmZone: (areaId) => {
        const placements = { ...get().placements }
        for (const id of Object.keys(placements)) {
          if (placements[id].areaId === areaId) placements[id] = { ...placements[id], confirmed: true }
        }
        set({ placements })
      },

      play: () => set({ playback: 'playing' }),
      pause: () => set({ playback: 'paused' }),
      seek: (t) => set({ currentTime: Math.max(get().windowStart, Math.min(get().windowEnd, t)) }),
      // Allow fractional (slow-mo) speeds — do NOT round, so 0.25× etc. survive.
      setSpeed: (n) => set({ speed: Math.max(MIN_SPEED, Math.min(MAX_SPEED, n)) }),

      tick: (realDeltaMs) => {
        const { currentTime, windowEnd, speed, playback } = get()
        if (playback !== 'playing') return
        const next = currentTime + realDeltaMs * SIM_MS_PER_REAL_MS_AT_1X * speed
        if (next >= windowEnd) {
          set({ currentTime: windowEnd, playback: 'paused' })
        } else {
          set({ currentTime: next })
        }
      },

      setMotionConfig: (patch) => set({ motionConfig: { ...get().motionConfig, ...patch } }),

      setPricing: (pricing) => set({ pricing }),

      setZonePricingOverride: (areaId, override) => {
        const next = { ...get().zonePricingOverrides }
        if (override === null) delete next[areaId]
        else next[areaId] = override
        set({ zonePricingOverrides: next })
      },

      setStatsPeriod: (period) => set({ statsPeriod: period }),

      resetStage: () => set({ stageEpoch: get().stageEpoch + 1 }),
    }),
    {
      name: PERSIST_KEY,
      partialize: (s): PersistedState => ({
        windowStart: s.windowStart,
        windowEnd: s.windowEnd,
        currentTime: s.currentTime,
        speed: s.speed,
        algorithm: s.algorithm,
        algorithmDirty: s.algorithmDirty,
        processedCount: s.processedCount,
        activeScenarioId: s.activeScenarioId,
        scenarioOptions: s.scenarioOptions,
        customScenarios: s.customScenarios,
        // compact: round feet to 1 decimal, rotation to int
        placements: Object.fromEntries(
          Object.entries(s.placements).map(([id, p]) => [
            id,
            {
              ...p,
              position: [Math.round(p.position[0] * 10) / 10, Math.round(p.position[1] * 10) / 10] as [
                number,
                number,
              ],
              rotation: Math.round(p.rotation),
            },
          ])
        ),
      }),
    }
  )
)

/** Selectors */
export const selectTotal = (s: State) => s.orderedScheduleIds.length
export const selectUnprocessed = (s: State) =>
  Math.max(0, s.orderedScheduleIds.length - s.processedCount)
export const selectPlacedCount = (s: State) => Object.keys(s.placements).length
/** Processed but couldn't get a spot (no compatible slot) — surfaced, never hidden. */
export const selectOverflow = (s: State) =>
  Math.max(0, s.processedCount - Object.keys(s.placements).length)

/** Total fixed slots across all zones — the concurrent capacity. */
export const selectCapacity = (s: State) => s.slotCapacity

/** Fixed slot count for one zone (its concurrent capacity). */
export const selectZoneSlotCount = (s: State, areaId: string): number =>
  s.slotGrids.find((g) => g.areaId === areaId)?.slots.length ?? 0

/** Peak concurrent occupancy of one zone across all placed stays. */
export const selectZonePeakConcurrent = (s: State, areaId: string): number =>
  peakConcurrent(
    Object.values(s.placements)
      .filter((p) => p.areaId === areaId)
      .map((p) => [p.arrival, p.departure] as [number, number])
  )

/** Peak concurrent occupancy across all placed stays (the true capacity metric). */
export const selectPeakConcurrent = (s: State) =>
  peakConcurrent(
    Object.values(s.placements).map((p) => [p.arrival, p.departure] as [number, number])
  )

/** Placements visible at a given time: arrival <= t < departure. */
export function selectVisiblePlacements(s: State, t: number): EventPlacement[] {
  return Object.values(s.placements).filter((p) => p.arrival <= t && t < p.departure)
}

/**
 * Derived financial + operational statistics, scoped to `statsPeriod`. Computed
 * in a hook (not a selector) because `computeEventStats` returns a fresh object
 * each call — memoizing over the primitive inputs keeps `useSyncExternalStore`
 * snapshots stable and recomputes only when an input actually changes.
 */
/** Per-area fit summary for the Zone Placement panel's capacity advice. */
export interface ZoneFit {
  /** fixed slots in the area (its concurrent capacity) */
  slotCount: number
  /** largest aircraft (along-axis footprint, ft) the area's slots can hold */
  maxSlotFt: number
  /** offered aircraft larger than this area's slots — they spill to larger areas */
  oversizeCount: number
  /** total offered fleet size */
  fleetCount: number
  /** aircraft that couldn't be placed in ANY area (processed but no slot) */
  overflow: number
}

/**
 * Fit summary for one area: capacity, the biggest hull it can park, how many of
 * the offered fleet are too large for it (they spill to larger areas via the
 * priority fill in `scheduleReuse`), and the global overflow. Powers the panel's
 * "what fits / enlarge the area" advice. Memoized like `useEventStats` so the
 * snapshot stays stable for `useSyncExternalStore`.
 */
export function useZoneFit(areaId: string | null): ZoneFit {
  const slotGrids = useEventPlannerStore((s) => s.slotGrids)
  const inquiriesById = useEventPlannerStore((s) => s.inquiriesById)
  const catalog = useEventPlannerStore((s) => s.catalog)
  const overflow = useEventPlannerStore(selectOverflow)
  return useMemo(() => {
    const grid = areaId ? slotGrids.find((g) => g.areaId === areaId) : undefined
    const slots = grid?.slots ?? []
    const maxSlotFt = slots.length ? Math.max(...slots.map((s) => s.extentFt)) : 0
    const axis = grid?.progression === 'nose_to_tail' ? 'length' : 'wingspan'
    const fleet = Object.values(inquiriesById)
    let oversize = 0
    for (const inq of fleet) {
      const fp = catalog.get(inq.typeCode)?.footprint
      if (fp && fp[axis] > maxSlotFt) oversize++
    }
    return { slotCount: slots.length, maxSlotFt, oversizeCount: oversize, fleetCount: fleet.length, overflow }
  }, [areaId, slotGrids, inquiriesById, catalog, overflow])
}

export function useEventStats(): EventStats {
  const inquiriesById = useEventPlannerStore((s) => s.inquiriesById)
  const placements = useEventPlannerStore((s) => s.placements)
  const catalog = useEventPlannerStore((s) => s.catalog)
  const pricing = useEventPlannerStore((s) => s.pricing)
  const zoneOverrides = useEventPlannerStore((s) => s.zonePricingOverrides)
  const zones = useEventPlannerStore((s) => s.zones)
  const period = useEventPlannerStore((s) => s.statsPeriod)
  return useMemo(
    () => computeEventStats({ inquiriesById, placements, catalog, pricing, zoneOverrides, zones, period }),
    [inquiriesById, placements, catalog, pricing, zoneOverrides, zones, period]
  )
}
